import { change, getFormValues, InjectedFormProps, reduxForm } from "redux-form";
import { size } from "lodash";
import { useEffect } from "react";

import { Button, Separator, TextDisplay, Wrapper } from "src/styled";
import { Input, IntlPhoneNumberInput, SeparatorWithText, TypedField } from "src/components";
import { isEmail, isIntlPhone } from "src/utils/validators";
import { translate } from "src/services";
import { useAppDispatch, useAppSelector } from "src/redux/store";

export const REQUEST_CODE_FORM_NAME = "requestCodeForm";

export interface LoginFormValues {
  email: string;
  phone: string;
}

interface PropsWithoutReduxForm {}

type Props = PropsWithoutReduxForm & InjectedFormProps<LoginFormValues, PropsWithoutReduxForm>;

const RequestCodeForm = ({ handleSubmit }: Props) => {
  const isSending = useAppSelector((state) => state.auth.isSendingCode);
  const dispatch = useAppDispatch();

  const formValues = useAppSelector(getFormValues(REQUEST_CODE_FORM_NAME)) as LoginFormValues;

  // if user enters phone number, clear email field
  useEffect(() => {
    if (size(formValues?.phone) > 2) {
      dispatch(change(REQUEST_CODE_FORM_NAME, "email", ""));
    }
  }, [dispatch, formValues?.phone]);

  // if user enters email, clear phone field
  useEffect(() => {
    if (size(formValues?.email) > 2) {
      dispatch(change(REQUEST_CODE_FORM_NAME, "phone", ""));
    }
  }, [dispatch, formValues?.email]);

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Wrapper padding="0 50px" maxWidth={500} margin="no auto">
        <TypedField
          component={Input}
          name="email"
          props={{
            type: "email",
            label: translate("common.emailAddress"),
            margin: "no auto",
            width: "80%",
          }}
          validate={[isEmail]}
        />
        <Separator height={50} />
        <SeparatorWithText text={translate("common.or")} />
        <Separator height={50} />
        <TypedField
          component={IntlPhoneNumberInput}
          name="phone"
          props={{
            initialValue: formValues?.phone || "",
            changeFieldValue: (value: string) => {
              dispatch(change(REQUEST_CODE_FORM_NAME, "phone", value));
            },
            margin: "no auto",
            width: "80%",
            placeholder: translate("common.phoneNumber"),
          }}
          validate={[isIntlPhone]}
        />
        <Separator height={120} />
        <Wrapper textAlign="center">
          <Button
            type="submit"
            color="primary"
            size="medium"
            margin="sMedium auto"
            borderRadius="100px"
            loadingIndicator={isSending}
          >
            {translate("loginPage.sendVerificationCode")}
          </Button>
          <TextDisplay block margin="small large" color="grayDarker">
            <em>{translate("loginPage.footerNote")}</em>
          </TextDisplay>
        </Wrapper>
        <Separator height={50} />
      </Wrapper>
    </form>
  );
};

export default reduxForm<LoginFormValues, PropsWithoutReduxForm>({
  form: REQUEST_CODE_FORM_NAME,
  initialValues: {
    email: "",
    phone: "",
  },
  enableReinitialize: true,
  validate: (values) => {
    const errors: Partial<LoginFormValues> = {};
    if (!values.email && !values.phone) {
      errors.email = translate("validationMessages.isRequiredEmailOrPhone");
      errors.phone = translate("validationMessages.isRequiredEmailOrPhone");
    }
    return errors;
  },
})(RequestCodeForm);
